import { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  Download,
  Trash2,
  ArrowUpDown,
  Eye,
  FileText,
  CheckCircle,
  Grid,
  List,
  X,
  FolderUp,
  MoreHorizontal,
  Box,
  AlertCircle,
  AlertTriangle,
  Info,
  XCircle,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  getFileUrn,
  deleteFileUrn,
  deleteFileUrnId,
  downloadFile,
  getUrnStatus,
} from "./api/index";
import styles from "./FileManager.module.css";
import useAutodeskViewer from "../../hooks/useAutodeskViewer";
import FileUploadService from "../../services/FileUploadService";
import { useAuth } from "../../context/AuthContext";
import maxlogo from "./img/3ds-max-logo.png";
import revitlogo from "./img/revit-logo.png";
import ifclogo from "./img/ifc-logo.png";
import dwglogo from "./img/dwg-logo.png";
import pdflogo from "./img/pdf-logo.webp";
import objlogo from "./img/obj-logo.webp";
const FILE_ICONS = {
  "3ds": maxlogo,
  max: maxlogo,
  rvt: revitlogo,
  rfa: revitlogo,
  rte: revitlogo,
  ifc: ifclogo,
  dwg: dwglogo,
  dxf: dwglogo,
  pdf: pdflogo,
  obj: objlogo,
};

// SVF conversion: a file can be opened in the viewer only when conversion_status is "success"
const isConverting = (file) =>
  typeof file.id === "number" &&
  file.conversion_status !== "success" &&
  file.conversion_status !== "failed";

const getConversionLabel = (file) => {
  if (
    file.conversion_status === "pending" ||
    file.conversion_status === "inprogress"
  ) {
    const percent = parseInt(file.conversion_progress, 10);
    return Number.isNaN(percent) ? "Converting..." : `Converting ${percent}%`;
  }
  if (file.conversion_status === "failed") return "Failed";
  return file.status || "Completed";
};

const getFileIcon = (filename) => {
  const ext = filename?.split(".").pop()?.toLowerCase();
  return FILE_ICONS[ext] || null;
};

const FileManager = () => {
  const {
    accessToken,
    viewer,
    status,
    loading,
    initializeApp,
    loadModel,
    fitToWindow,
    resetView,
    toggleFullscreen,
  } = useAutodeskViewer();
  const navigate = useNavigate();

  const { onLogout } = useAuth();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileVersion, setFileVersion] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterType, setFilterType] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]); // Ընտրված մոդելները compare-ի համար
  const [showFileMenu, setShowFileMenu] = useState(false);

  // ==== Upload dialog state-եր (ամբողջությամբ ինքնուրույն, FileManager-ի ներսում) ====
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [alertModal, setAlertModal] = useState({
    show: false,
    type: "warning", // "error" | "warning" | "info"
    title: "",
    message: "",
  });

  const ALERT_DEFAULT_TITLES = {
    error: "Error",
    warning: "Warning",
    info: "Information",
  };

  const showAlert = (message, { type = "warning", title } = {}) => {
    setAlertModal({
      show: true,
      type,
      title: title || ALERT_DEFAULT_TITLES[type],
      message,
    });
  };

  const closeAlert = () => setAlertModal((prev) => ({ ...prev, show: false }));

  // Escape-ով փակելու համար
  useEffect(() => {
    if (!alertModal.show) return;
    const onKey = (e) => e.key === "Escape" && closeAlert();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [alertModal.show]);

  useEffect(() => {
    fetchFiles();
  }, []);

  // Uploads from the header (navBarAuth) announce themselves so the list picks them up
  useEffect(() => {
    const onUploaded = () => fetchFiles();
    window.addEventListener("viewstruct:file-uploaded", onUploaded);
    return () =>
      window.removeEventListener("viewstruct:file-uploaded", onUploaded);
  }, []);

  // Poll conversion status every 5s while any file is still converting
  useEffect(() => {
    const converting = files.filter(isConverting);
    if (!converting.length) return;
    const timer = setTimeout(async () => {
      const results = await Promise.all(
        converting.map((f) => getUrnStatus(f.id).catch(() => null)),
      );
      const byId = {};
      results.filter(Boolean).forEach((s) => (byId[s.id] = s));
      setFiles((prev) =>
        prev.map((f) =>
          byId[f.id]
            ? {
                ...f,
                conversion_status: byId[f.id].conversion_status,
                conversion_progress: byId[f.id].conversion_progress,
                conversion_error: byId[f.id].conversion_error,
              }
            : f,
        ),
      );
    }, 5000);
    return () => clearTimeout(timer);
  }, [files]);

  const handleSelectFile = (clickedFile) => {
    // selectedFile (preview panel) can be a stale copy; use the polled one
    const selectedFile =
      files.find((f) => f.id === clickedFile?.id) || clickedFile;
    if (!selectedFile?.urn) return;

    // if (selectedFile.conversion_status === "failed") {
    //   alert(
    //     `Conversion failed: ${selectedFile.conversion_error || "unknown error"}`,
    //   );
    //   return;
    // }
    // if (
    //   selectedFile.conversion_status === "pending" ||
    //   selectedFile.conversion_status === "inprogress"
    // ) {
    //   alert("The file is still being converted. Please try again in a moment.");
    //   return;
    // }

    if (selectedFile.conversion_status === "failed") {
      showAlert(selectedFile.conversion_error || "Unknown error", {
        type: "error",
        title: "Conversion failed",
      });
      return;
    }
    if (
      selectedFile.conversion_status === "pending" ||
      selectedFile.conversion_status === "inprogress"
    ) {
      showAlert(
        "The file is still being converted. Please try again in a moment.",
        {
          type: "info",
          title: "Conversion in progress",
        },
      );
      return;
    }

    const encodedUrn = encodeURIComponent(selectedFile.urn);
    const urnId =
      selectedFile.id != null
        ? `&urnId=${encodeURIComponent(String(selectedFile.id))}`
        : "";

    navigate(`/ViewerPage?urn=${encodedUrn}${urnId}`);
  };

  const handleSelectModelForCompare = (file, event) => {
    event?.stopPropagation();

    setSelectedModels((prev) => {
      const isAlreadySelected = prev.some((m) => m.id === file.id);

      if (isAlreadySelected) {
        return prev.filter((m) => m.id !== file.id);
      } else {
        if (prev.length >= 2) {
          // alert("You can select maximum 2 models for comparison");
          showAlert("You can select maximum 2 models for comparison.", {
            title: "Selection limit",
          });

          return prev;
        }
        return [...prev, file];
      }
    });
  };
  const handleCompareModels = () => {
    if (selectedModels.length !== 2) {
      // alert("Please select exactly 2 models to compare");

      showAlert("Please select exactly 2 models to compare.", {
        title: "Select 2 models",
      });
      return;
    }

    if (!selectedModels[0]?.urn || !selectedModels[1]?.urn) {
      // alert("Selected models are missing URN data. Check console.");

      showAlert("Selected models are missing URN data.", {
        type: "error",
        title: "Cannot compare",
      });
      return;
    }

    const urn1 = encodeURIComponent(selectedModels[0].urn);
    const urn2 = encodeURIComponent(selectedModels[1].urn);

    const urnId =
      selectedModels[0].id != null
        ? `&urnId=${encodeURIComponent(String(selectedModels[0].id))}`
        : "";

    const target = `/ViewerPage?urn1=${urn1}&urn2=${urn2}${urnId}`;
    navigate(target);
  };

  const handleClearSelection = () => {
    setSelectedModels([]);
  };

  const fetchFiles = async () => {
    try {
      setIsLoading(true);
      const response = await getFileUrn();
      setFiles(response || []);

      if (response && response.length > 0 && !selectedFile) {
        setSelectedFile(response[0]);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredFiles = files
    .filter((file) => {
      const matchesSearch = file.description
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

      if (filterType === "all") return matchesSearch;

      const fileExtension = file.description?.split(".").pop()?.toLowerCase();
      return matchesSearch && fileExtension === filterType;
    })
    .sort((a, b) => {
      let comparison = 0;

      if (sortBy === "name") {
        comparison = a.description?.localeCompare(b.description);
      } else if (sortBy === "size") {
        comparison = (a.size || 0) - (b.size || 0);
      } else if (sortBy === "date") {
        comparison = new Date(a.updated_at) - new Date(b.updated_at);
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

  filteredFiles.map((file) => {
    return file; // map-ը պետք է վերադարձնի ինչ-որ բան, հակառակ դեպքում կարող է առաջացնել սխալ
  });

  const handleDeleteClick = async (file, event) => {
    event?.stopPropagation();
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    try {
      setIsDeleting(true);
      await deleteFileUrnId(fileToDelete.id);

      const updatedFiles = files.filter((f) => f.id !== fileToDelete.id);
      setFiles(updatedFiles);

      setSelectedModels((prev) => prev.filter((m) => m.id !== fileToDelete.id));

      if (selectedFile?.id === fileToDelete.id) {
        setSelectedFile(updatedFiles[0] || null);
      }

      setShowDeleteModal(false);
      setFileToDelete(null);
    } catch (error) {
      console.error("Error deleting file:", error);
      showAlert("Failed to delete file. Please try again.", {
        type: "error",
        title: "Delete failed",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async (file, event) => {
    event?.stopPropagation();
    try {
      const blob = await downloadFile(file.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.description || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      // alert(`Failed to download file: ${error.message}`);
      showAlert(`Failed to download file: ${error.message}`, {
        type: "error",
        title: "Download failed",
      });
    }
  };

  const addUploadedFileToList = (newFile) => {
    const fileToAdd = {
      id:
        newFile.urnId || newFile.id || newFile.uploadId || `file-${Date.now()}`,
      description:
        newFile.description || newFile.name || newFile.fileName || "Unknown",
      original_filename: newFile.fileName,
      conversion_status: newFile.conversion_status,
      urn: newFile.urn || newFile.objectKey || "",
      updated_at:
        newFile.updated_at || newFile.createdAt || new Date().toISOString(),
      size: newFile.size || newFile.fileSize || 0,
    };

    setFiles((prevFiles) => [fileToAdd, ...prevFiles]);
    setSelectedFile(fileToAdd);
    setFileVersion((v) => v + 1);
  };

  const openUploadDialog = () => {
    setShowUploadDialog(true);
    setIsUploading(false);
    setUploadProgress(0);
    setUploadError("");
    setUploadingFileName("");
    setIsDragging(false);
  };

  const closeUploadDialog = () => {
    if (isUploading) return;
    setShowUploadDialog(false);
    setIsUploading(false);
    setUploadProgress(0);
    setUploadError("");
    setUploadingFileName("");
  };

  const startUpload = (file) => {
    if (!file) return;

    const backendAuthToken = localStorage.getItem("accessToken");
    if (!backendAuthToken) {
      setUploadError("You are not logged in. Please log in and try again.");
      return;
    }

    setUploadError("");
    setUploadingFileName(file.name);

    setIsUploading(true);
    setUploadProgress(0);

    let trickleTimer = null;
    const startTrickle = () => {
      if (trickleTimer) return;
      trickleTimer = setInterval(() => {
        setUploadProgress((prev) => (prev < 96 ? prev + 1 : prev));
      }, 400);
    };
    const stopTrickle = () => {
      if (trickleTimer) {
        clearInterval(trickleTimer);
        trickleTimer = null;
      }
    };

    FileUploadService.uploadFile(file, accessToken, (progressPercent) => {
      // Իրական transfer progress՝ սահմանափակված 90%-ով
      const capped = Math.min(progressPercent, 90);
      setUploadProgress((prev) => (capped > prev ? capped : prev));

      if (progressPercent >= 100) {
        startTrickle();
      }
    })
      .then((result) => {
        stopTrickle();
        setUploadProgress(100);
        addUploadedFileToList(result);
        setTimeout(() => {
          setIsUploading(false);
          closeUploadDialog();
        }, 700);
      })
      .catch((error) => {
        stopTrickle();
        console.error("Upload error:", error);
        setUploadError(error.message || "Upload failed");
        setIsUploading(false);
        setUploadProgress(0);
      });
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) startUpload(file);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) startUpload(file);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    const mb = kb / 1024;

    if (mb >= 1) {
      return `${mb.toFixed(1)} MB`;
    }
    return `${kb.toFixed(0)} KB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getFileExtension = (filename) => {
    return filename?.split(".").pop()?.toUpperCase() || "FILE";
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleLogOut = useCallback(async () => {
    setSubmitting(true);
    try {
      await onLogout();
      setSubmitting(false);
      navigate("/");
    } catch (err) {
      setSubmitting(false);
    }
  }, [onLogout, navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.selectionBar}>
        <div className={styles.selectionInfo}>
          <strong>Selected for comparison:</strong>
          {selectedModels.length === 0 ? (
            <span className={styles.noSelection}> No models selected</span>
          ) : (
            <div className={styles.selectedModelsList}>
              {selectedModels.map((model) => (
                <span key={model.id} className={styles.selectedModelTag}>
                  {model.description}
                  <button
                    className={styles.removeSelected}
                    onClick={(e) => handleSelectModelForCompare(model, e)}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.selectionActions}>
          {selectedModels.length > 0 && (
            <>
              <button
                className={styles.clearSelectionBtn}
                onClick={handleClearSelection}
              >
                Clear
              </button>
              <button
                className={styles.compareBtn}
                onClick={handleCompareModels}
                disabled={selectedModels.length !== 2}
              >
                Compare Models ({selectedModels.length}/2)
              </button>
            </>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.listPanel}>
          <div className={styles.searchBar}>
            <div className={styles.searchInput}>
              <Search size={20} className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className={styles.toolbar}>
              <button
                className={styles.uploadHeaderBtn}
                onClick={openUploadDialog}
                title="Upload file"
              >
                <FolderUp size={18} />
                Upload
              </button>
              <button
                className={styles.toolButton}
                onClick={() => toggleSort("date")}
                title="Sort by date"
              >
                <ArrowUpDown size={20} />
              </button>
              <button
                className={styles.toolButton}
                onClick={() =>
                  setViewMode(viewMode === "list" ? "grid" : "list")
                }
                title="Toggle view"
              >
                {viewMode === "list" ? <Grid size={20} /> : <List size={20} />}
              </button>
            </div>
          </div>

          <div className={styles.fileStats}>
            <span className={styles.statItem}>
              {filteredFiles.length} files
            </span>
          </div>

          {showUploadDialog && (
            <div className={styles.uploadDialog}>
              <div className={styles.uploadDialogContent}>
                <button
                  className={styles.closeButton}
                  onClick={closeUploadDialog}
                  disabled={isUploading}
                  title="Close"
                >
                  <X size={20} />
                </button>

                <div className={styles.uploadIcon}>
                  <FolderUp size={48} />
                </div>

                <h3 className={styles.uploadTitle}>
                  {isUploading ? "Uploading..." : "Upload CAD File"}
                </h3>
                <p className={styles.uploadDescription}>
                  {isUploading
                    ? uploadingFileName ||
                      "Please wait while your file is being uploaded"
                    : "Drag and drop your file or click to browse"}
                </p>

                {isUploading && (
                  <div className={styles.circularProgress}>
                    <svg
                      className={styles.progressRing}
                      width="120"
                      height="120"
                    >
                      <circle
                        className={styles.progressRingCircle}
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        r="52"
                        cx="60"
                        cy="60"
                      />
                      <circle
                        className={styles.progressRingCircleFill}
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        r="52"
                        cx="60"
                        cy="60"
                        style={{
                          strokeDasharray: `${2 * Math.PI * 52}`,
                          strokeDashoffset: `${
                            2 * Math.PI * 52 * (1 - uploadProgress / 100)
                          }`,
                        }}
                      />
                    </svg>
                    <span className={styles.progressPercentage}>
                      {uploadProgress}%
                    </span>
                  </div>
                )}

                {uploadError && (
                  <p className={styles.uploadErrorText}>{uploadError}</p>
                )}

                {!isUploading && (
                  <>
                    <div
                      className={`${styles.dropZone} ${
                        isDragging ? styles.dropZoneActive : ""
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FolderUp size={36} className={styles.dropZoneIcon} />
                      <p>Drop file here or click to browse</p>
                      <span className={styles.dropZoneHint}>
                        Supported: .rvt, .ifc, .dwg, .nwd, .3ds, .fbx, .obj...
                      </span>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleFileInputChange}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          <div className={styles.fileTableWrapper} key={fileVersion}>
            {isLoading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Loading files...</p>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className={styles.emptyState}>
                <FileText size={48} />
                <p>No files found</p>
                <div className={styles.uploadSection}>
                  <button
                    className={styles.emptyUploadBtn}
                    onClick={openUploadDialog}
                  >
                    <FolderUp size={18} />
                    Upload a file
                  </button>
                </div>
              </div>
            ) : (
              <table className={styles.fileTable}>
                <thead>
                  <tr>
                    <th className={styles.checkboxCol}>
                      <input type="checkbox" disabled />
                    </th>
                    <th>Name</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Size</th>
                    <th>Issues</th>
                    <th>Created In</th>
                    <th className={styles.actionsCol}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.map((file) => (
                    <tr
                      key={file.id}
                      className={`${styles.tableRow} ${
                        selectedFile?.id === file.id
                          ? styles.tableRowSelected
                          : ""
                      } ${
                        selectedModels.some((m) => m.id === file.id)
                          ? styles.tableRowChecked
                          : ""
                      }`}
                      onClick={() => setSelectedFile(file)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedModels.some((m) => m.id === file.id)}
                          onChange={(e) => handleSelectModelForCompare(file, e)}
                        />
                      </td>

                      <td>
                        {/* <div className={styles.nameCell}>
                          <div className={styles.tableFileIcon}>
                            <span className={styles.miniIconLetter}>
                              {getFileExtension(file.description)[0]}
                            </span>
                          </div>
                          <span className={styles.tableFileName}>
                            {file.original_filename || file.description}
                          </span>
                        </div> */}

                        <div className={styles.nameCell}>
                          {getFileIcon(
                            file.original_filename || file.description,
                          ) ? (
                            <img
                              src={getFileIcon(
                                file.original_filename || file.description,
                              )}
                              alt={getFileExtension(file.description)}
                              className={styles.tableFileImg}
                            />
                          ) : (
                            <div className={styles.tableFileIcon}>
                              <span className={styles.miniIconLetter}>
                                {getFileExtension(file.description)[0]}
                              </span>
                            </div>
                          )}
                          <span className={styles.tableFileName}>
                            {file.original_filename || file.description}
                          </span>
                        </div>
                      </td>

                      <td className={styles.versionCell}>
                        V{file.version || 1}
                      </td>

                      <td>
                        <span className={styles.statusBadge}>
                          <span className={styles.statusDot} />
                          {getConversionLabel(file)}
                        </span>
                      </td>

                      <td>{formatFileSize(file.file_size)}</td>

                      <td>{file.issues ?? 0}</td>

                      <td>{formatDate(file.updated_at)}</td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <div className={styles.tableActions}>
                          <button
                            className={styles.iconButton}
                            onClick={() => handleSelectFile(file)}
                            title="View"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className={styles.iconButton}
                            onClick={(e) => handleDownload(file, e)}
                            title="Download"
                          >
                            <Download size={18} />
                          </button>
                          <button
                            className={styles.iconButton}
                            onClick={(e) => handleDeleteClick(file, e)}
                            title="More"
                          >
                            <MoreHorizontal size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className={styles.previewPanel}>
          <div className={styles.previewCard}>
            {selectedFile ? (
              <>
                <button
                  className={styles.previewMenuBtn}
                  onClick={() => setShowFileMenu((v) => !v)}
                  title="More"
                >
                  <MoreHorizontal size={20} />
                </button>

                {showFileMenu && (
                  <div className={styles.previewMenuDropdown}>
                    <button
                      onClick={(e) => {
                        handleDownload(selectedFile, e);
                        setShowFileMenu(false);
                      }}
                    >
                      <Download size={16} /> Download
                    </button>
                    <button
                      onClick={(e) => {
                        handleDeleteClick(selectedFile, e);
                        setShowFileMenu(false);
                      }}
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                )}

                {/* <div className={styles.previewThumb}>
                  <Box size={90} className={styles.previewThumbIcon} />
                  <span className={styles.previewExtBadge}>
                    {getFileExtension(selectedFile.description)}
                  </span>
                </div> */}
                <div className={styles.previewThumb}>
                  {getFileIcon(selectedFile.description) ? (
                    <img
                      src={getFileIcon(selectedFile.description)}
                      alt={getFileExtension(selectedFile.description)}
                      className={styles.previewThumbImg}
                    />
                  ) : (
                    <Box size={90} className={styles.previewThumbIcon} />
                  )}
                  <span className={styles.previewExtBadge}>
                    {getFileExtension(selectedFile.description)}
                  </span>
                </div>
                <h3 className={styles.previewFileName}>
                  {selectedFile.description}
                </h3>
                <p className={styles.previewDate}>
                  {formatDate(selectedFile.updated_at)}
                </p>

                <div className={styles.previewDetails}>
                  <div className={styles.previewDetailRow}>
                    <span className={styles.previewDetailLabel}>Status</span>
                    <span className={styles.previewBadgeGray}>
                      <span className={styles.badgeDotGray} />V
                      {selectedFile.version || 1}
                    </span>
                  </div>
                  <div className={styles.previewDetailRow}>
                    <span className={styles.previewDetailLabel}>Version</span>
                    <span className={styles.previewBadgeGreen}>
                      <CheckCircle size={14} />
                      {selectedFile.status || "Completed"}
                    </span>
                  </div>
                  <div className={styles.previewDetailRow}>
                    <span className={styles.previewDetailLabel}>Size</span>
                    <span className={styles.previewDetailValue}>
                      {formatFileSize(selectedFile.file_size)}
                    </span>
                  </div>
                  <div className={styles.previewDetailRow}>
                    <span className={styles.previewDetailLabel}>
                      Created In
                    </span>
                    <span className={styles.previewDetailValue}>
                      {formatDate(selectedFile.updated_at)}
                    </span>
                  </div>
                </div>

                <div className={styles.previewActionsRow}>
                  <button
                    className={styles.previewDownloadBtn}
                    onClick={(e) => handleDownload(selectedFile, e)}
                  >
                    <Download size={18} />
                    Download
                  </button>
                  <button
                    className={styles.previewViewBtn}
                    onClick={() => handleSelectFile(selectedFile)}
                  >
                    <Eye size={18} />
                    View Model
                  </button>
                </div>
              </>
            ) : (
              <div className={styles.emptyPreview}>
                <FileText size={64} className={styles.emptyIcon} />
                <p>Select a file to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <AlertCircle size={24} className={styles.modalIcon} />
              <h3>Delete File</h3>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete{" "}
                <strong>{fileToDelete?.description}</strong>?
              </p>
              <p className={styles.modalWarning}>
                This action cannot be undone.
              </p>
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelBtn}
                onClick={() => {
                  setShowDeleteModal(false);
                  setFileToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className={styles.modalDeleteBtn}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {alertModal.show &&
        (() => {
          const ALERT_ICONS = {
            error: <XCircle size={24} className={styles.alertIconError} />,
            warning: (
              <AlertTriangle size={24} className={styles.alertIconWarning} />
            ),
            info: <Info size={24} className={styles.alertIconInfo} />,
          };
          const headerClass = {
            error: styles.alertHeaderError,
            warning: styles.alertHeaderWarning,
            info: styles.alertHeaderInfo,
          }[alertModal.type];

          return (
            <div className={styles.modalOverlay} onClick={closeAlert}>
              <div
                className={styles.modal}
                role="alertdialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`${styles.modalHeader} ${headerClass}`}>
                  {ALERT_ICONS[alertModal.type]}
                  <h3>{alertModal.title}</h3>
                </div>
                <div className={styles.modalBody}>
                  <p>{alertModal.message}</p>
                </div>
                <div className={styles.modalActions}>
                  <button
                    className={styles.alertOkBtn}
                    onClick={closeAlert}
                    autoFocus
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default FileManager;
